/**
 * Advanced Search UI DOM Event Handler. Responsible for handling
 * user interaction.
 */

/**
 * Advanced Search UI DOM Event Handler constructor
 *
 * @param {Object} messageBus  WH message bus
 * @param {Object} dom  DOM objects
 *
 * @constructor
 */
function AdvancedSearchUiDomEventHandler(dom) {

    this.dom = dom;
    this.bindAllDomEvents();
}

/**
 * Binding all the user generated DOM events
 *
 * @returns {void}
 *
 * @private
 */
AdvancedSearchUiDomEventHandler.prototype.bindAllDomEvents = function () {
    this.bindWindowKeyDownEvent();
    this.bindSearchOpenClickEvent();
    this.bindSearchCloseClickEvent();
};

/**
 * Rerturn a function that triggers internal event and calls the passed handler function
 *
 * @param {String} internalEventName
 * @param {Function} eventHandlerCallBack
 *
 * @returns {Function}
 *
 * @private
 */
AdvancedSearchUiDomEventHandler.prototype.triggerInternalEventAndCallHandler = function (internalEventName,
    eventHandlerCallBack) {
    return function (event) {
        if (typeof eventHandlerCallBack === 'function') {
            eventHandlerCallBack(event);
        }

        if (internalEventName === 'search.eventResultItem.click' ||
            internalEventName === 'search.iconBarItem.click' ||
            internalEventName === 'search.close') {
            document.body.style.overflow = 'unset';
        }

        if (window.WH && window.WH.messageBus) {
            window.WH.messageBus.publish(internalEventName, event);
        }
    }.bind(this);
};

/**
 * Binds an event to the relevant DOM node and assign a handler function
 *
 * @param domEventName
 * @param internalEventName
 * @param domObject
 * @param descendantsSelectors
 * @param eventHandlerCallBack
 *
 * @returns {void}
 *
 * @private
 */
AdvancedSearchUiDomEventHandler.prototype.bindAnEventToTheDom = function (domEventName, internalEventName, domObject,
    descendantsSelectors, eventHandlerCallBack) {

    domObject.off(domEventName);

    if (typeof descendantsSelectors !== 'undefined') {

        domObject.on(domEventName, descendantsSelectors, this.triggerInternalEventAndCallHandler(
            internalEventName,
            eventHandlerCallBack
        ));
    } else {

        domObject.on(domEventName, this.triggerInternalEventAndCallHandler(
            internalEventName,
            eventHandlerCallBack
        ));
    }
};

/**
 * Binding an event to DOM elements and triggering another internal event
 *
 * @param domEventName {String} the DOM event to bind (eg: 'click.search.close')
 * @param internalEventName {String} the internal event to trigger (eg: 'search.close')
 * @param descendant {String} dom selectors (coma separated. eg: 'p > a, li a, td > a')
 *
 * @returns {void}
 *
 * @private
 */
AdvancedSearchUiDomEventHandler.prototype.bindAnEventToTheDomUnderOverlayAndTriggerAnotherInternalEvent =
    function (domEventName, internalEventName, descendant) {

        this.bindAnEventToTheDom(
            domEventName,
            internalEventName,
            this.dom.overlay,
            descendant.selector
        );
    };

/**
 *
 * @param event
 */
AdvancedSearchUiDomEventHandler.prototype.defaultEventHandler = function (event) {

    event.preventDefault();
    event.stopPropagation();
};

/**
 * Binding the click event handlers on the Search Open buttons
 *
 * @returns {void}
 *
 * @private
 */
AdvancedSearchUiDomEventHandler.prototype.bindSearchOpenClickEvent = function () {

    var openSearchEventHandler = function (event) {
        this.defaultEventHandler(event);
    }.bind(this);

    this.bindAnEventToTheDom(
        'click.search.open',
        'search.open',
        this.dom.open,
        undefined,
        openSearchEventHandler
    );
};

/**
 * Binding the click event handler on the Search Close button
 *
 * @returns {void}
 *
 * @private
 */
AdvancedSearchUiDomEventHandler.prototype.bindSearchCloseClickEvent = function () {

    this.bindAnEventToTheDomUnderOverlayAndTriggerAnotherInternalEvent(
        'click.search.close',
        'search.close',
        this.dom.close
    );
};

/**
 * Binding the key down event handler on the window
 *
 * @returns {void}
 *
 * @private
 */
AdvancedSearchUiDomEventHandler.prototype.bindWindowKeyDownEvent = function () {

    var internalKeyDownEventName = 'search.window.keydown';
    var internalCloseEventName = 'search.close';
    var ESCAPE_KEY = 27;

    this.dom.document.on('keydown.search.document', function (keyDownEvent) {

        if (window.WH && window.WH.messageBus) {
            window.WH.messageBus.publish(internalKeyDownEventName, keyDownEvent);

            if (keyDownEvent && keyDownEvent.keyCode === ESCAPE_KEY) {
                window.WH.messageBus.publish(internalCloseEventName, keyDownEvent);
            }
        }
    }.bind(this));
};

export default AdvancedSearchUiDomEventHandler;
